import { isKeyOf } from 'helpers/types/utils';
import { SPLIT_BY } from 'routes/BudgetModule/config/constants';
import {
    DateString,
    isDatedField,
    SimulationData,
    SimulationOnlyData,
} from 'routes/HumanResourcesModule/business/types';
import {
    AgentHistoricData,
    AgentPreviousSimulationData,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/types';
import {
    EnvelopeHistoricData,
    EnvelopePreviousSimulationData,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/helpers/types';
import { SIMULATED_AMOUNT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';

export const PREVIOUS_SIMULATION_TYPE_OF_LINE = 'typeOfLine';

type KeysToExclude =
    | `historicAmount..${number}${number}${number}`
    | `simulatedAmount..${number}${number}${number}`;

export type HistoricDataFilters =
    | Partial<Omit<EnvelopeHistoricData, KeysToExclude>>
    | Partial<Omit<EnvelopeHistoricData, KeysToExclude>>;
export type PreviousDataFilters =
    | Partial<Omit<EnvelopePreviousSimulationData, KeysToExclude>>
    | Partial<Omit<AgentPreviousSimulationData, KeysToExclude>>;

type PreviousSimulationDate =
    | EnvelopePreviousSimulationData
    | AgentPreviousSimulationData;
type HistoricData = EnvelopeHistoricData | AgentHistoricData;

export const getFilteredHistoricDataForModal = ({
    historicData,
    filters,
}: {
    historicData: HistoricData[];
    filters: HistoricDataFilters;
}) => {
    const filteredHistoricData = historicData.filter((dataItem) =>
        Object.keys(filters).every((filterKey) => {
            if (isKeyOf(dataItem, filterKey)) {
                return dataItem[filterKey] === filters[filterKey];
            }
            return false;
        }),
    );

    return filteredHistoricData.length === 1 ? filteredHistoricData : [];
};

const filterPreviousSimulationDataForModal = ({
    previousSimulationData,
    filters,
}: {
    previousSimulationData: PreviousSimulationDate[];
    filters: PreviousDataFilters;
}): PreviousSimulationDate[] =>
    previousSimulationData.filter((dataItem) =>
        Object.keys(filters).every(
            (filterKey) => dataItem[filterKey] === filters[filterKey],
        ),
    );

/**
 * Given a data array, aggregates all the dated fields into one
 * object that sums all the elements of the array.
 * @param previousSimulationData
 * @returns
 */
const aggregateDetailedSimulationDataForModal = (
    previousSimulationData: PreviousSimulationDate[],
): SimulationOnlyData => {
    const result: SimulationOnlyData = {};
    previousSimulationData.forEach((d) => {
        Object.keys(d).forEach((k) => {
            if (!isDatedField(k, SIMULATED_AMOUNT)) return;
            if (!(k in result)) {
                result[k] = 0;
            }
            result[k] += d[k];
        });
    });
    return result;
};

export const getPreviousSimulationDataForModal = ({
    previousSimulationData,
    filters,
}: {
    previousSimulationData: EnvelopePreviousSimulationData[];
    filters: PreviousDataFilters;
}): SimulationOnlyData =>
    aggregateDetailedSimulationDataForModal(
        filterPreviousSimulationDataForModal({
            previousSimulationData,
            filters,
        }),
    );

/**
 * Represent the kind of line we are showing the in the modal.
 * The grid has two lines, one with real data, and one with fake
 * data that we use as user input. We use this enum to differentiate
 * the two lines.
 */
export enum PreviousSimulationTypeOfLine {
    REAL_DATA = 'realData',
    USER_INPUT = 'userInput',
}

export const getPreviousSimulationData = (
    previousSimulationDataByYear: SimulationOnlyData,
    valuesByDates: Record<DateString, number>,
): SimulationData<{
    [PREVIOUS_SIMULATION_TYPE_OF_LINE]: PreviousSimulationTypeOfLine;
}>[] => [
    {
        ...previousSimulationDataByYear,
        [PREVIOUS_SIMULATION_TYPE_OF_LINE]:
            PreviousSimulationTypeOfLine.REAL_DATA,
    },
    {
        ...Object.fromEntries(
            Object.keys(valuesByDates).map((k) => [
                `${SIMULATED_AMOUNT}${SPLIT_BY}${k}`,
                valuesByDates[k],
            ]),
        ),
        [PREVIOUS_SIMULATION_TYPE_OF_LINE]:
            PreviousSimulationTypeOfLine.USER_INPUT,
    },
];

export const keepOnlyChangedDates = (
    previousSimulationDataByYear: SimulationOnlyData,
    valuesByDates: Record<DateString, number>,
): Record<DateString, number> => {
    const result: Record<DateString, number> = {};
    Object.keys(valuesByDates).forEach((k) => {
        const wasEdited =
            valuesByDates[k] !==
            (previousSimulationDataByYear[
                `${SIMULATED_AMOUNT}${SPLIT_BY}${k}`
            ] ?? 0);
        if (!wasEdited) return;
        result[k] = valuesByDates[k];
    });
    return result;
};
